import type { ReactElement } from 'react';
import { HOVEDKONTO_KATEGORIER, type HovedkontoKategori } from '@es/shared';
import { useFunksjonsflagg, useKontoplan, useMeg, useVirksomheter } from '../api/sporringer.js';
import { Kort } from '../komponenter/Kort.js';
import { Feilmelding, Laster } from '../komponenter/Tilstand.js';
import { Oppgaver } from './Oppgaver.js';
import { serInvesteringene } from './investeringer/investeringer.js';
import { erBareFolkevalgt } from './rapportering/behandling.js';

const KATEGORINAVN: Record<HovedkontoKategori, string> = {
  lonn: 'Lønn',
  utgifter: 'Utgifter',
  inntekter: 'Inntekter',
};

/**
 * The landing view.
 *
 * Deliberately a SHELL, not Finansiell oversikt: this is scaffolding, and it
 * shows that the whole chain works end to end — OIDC login, tenant-scoped API
 * calls, Zod-validated responses, feature flags — without pretending that the
 * financial module in §4 has been built.
 */
export function Oversikt(): ReactElement {
  const virksomheter = useVirksomheter();
  const kontoplan = useKontoplan();
  const flagg = useFunksjonsflagg();
  const meg = useMeg();
  const erPa = (
    nokkel:
      | 'prognose'
      | 'rapportering'
      | 'budsjett'
      | 'moter'
      | 'investeringer'
      | 'vedlikehold'
      | 'kpi_og_tiltak',
  ): boolean => flagg.data?.some((f) => f.nokkel === nokkel && f.aktivert) === true;

  const investeringer =
    erPa('investeringer') && meg.data !== undefined && serInvesteringene(meg.data);

  // En folkevalgt har ingen virksomheter og ingen kontoplan å forholde seg
  // til — bare rapportene utvalget har fått. Resten av forsiden ville sagt
  // «be administratoren om tilgang» til noen som ikke skal ha den.
  if (meg.data !== undefined && erBareFolkevalgt(meg.data)) {
    return (
      <>
        <h1 className="text-2xl font-semibold text-tekst">Oversikt</h1>
        <p className="mt-2 max-w-2xl text-sm text-tekst-dempet">
          Rapportene som er sendt til utvalgene dine, og det du er bedt om å lese.
        </p>
        {erPa('rapportering') ? <Oppgaver prognose={false} rapportering budsjett={false} /> : null}
      </>
    );
  }

  return (
    <>
      <h1 className="text-2xl font-semibold text-tekst">Oversikt</h1>
      <p className="mt-2 max-w-2xl text-sm text-tekst-dempet">
        Det som venter på deg, virksomhetene du har tilgang til, og kommunens oppsett. Modulene slås
        på per kommune, så kommunen kan ta løsningen i bruk modul for modul.
      </p>

      {/* Øverst, fordi det er det eneste her som ber om noe. */}
      {erPa('prognose') ||
      erPa('rapportering') ||
      erPa('budsjett') ||
      erPa('moter') ||
      erPa('vedlikehold') ||
      erPa('kpi_og_tiltak') ||
      investeringer ? (
        <Oppgaver
          prognose={erPa('prognose')}
          rapportering={erPa('rapportering')}
          budsjett={erPa('budsjett')}
          moter={erPa('moter')}
          investeringer={investeringer}
          vedlikehold={erPa('vedlikehold')}
          saker={erPa('moter') || erPa('kpi_og_tiltak') || erPa('vedlikehold')}
        />
      ) : null}

      <div className="mt-8 grid gap-6 md:grid-cols-2">
        <Kort tittel="Virksomheter" beskrivelse="Virksomheter du har tilgang til.">
          {virksomheter.isPending ? <Laster hva="virksomheter" /> : null}
          {virksomheter.isError ? <Feilmelding feil={virksomheter.error} /> : null}
          {virksomheter.data?.length === 0 ? (
            <p className="text-sm text-tekst-dempet">
              Du har ikke fått tilgang til noen virksomheter ennå. Be administratoren i kommunen om
              det.
            </p>
          ) : null}
          {virksomheter.data ? (
            <ul className="space-y-2">
              {virksomheter.data.map((v) => (
                <li key={v.id} className="flex items-baseline justify-between gap-4">
                  <span className="text-sm text-tekst">{v.navn}</span>
                  <span className="text-xs text-tekst-dempet">{v.kommunalomradeNavn}</span>
                </li>
              ))}
            </ul>
          ) : null}
        </Kort>

        <Kort tittel="Kontoplan" beskrivelse="Tre nivåer, delt av alle virksomheter i kommunen.">
          {kontoplan.isPending ? <Laster hva="kontoplanen" /> : null}
          {kontoplan.isError ? <Feilmelding feil={kontoplan.error} /> : null}
          {kontoplan.data ? (
            <table className="w-full text-sm">
              <caption className="sr-only">
                Antall artsgrupper og underkontoer per hovedkonto
              </caption>
              <thead>
                <tr className="border-b border-kant text-left text-xs uppercase text-tekst-dempet">
                  <th scope="col" className="pb-2 font-medium">
                    Hovedkonto
                  </th>
                  <th scope="col" className="pb-2 text-right font-medium">
                    Artsgrupper
                  </th>
                  <th scope="col" className="pb-2 text-right font-medium">
                    Underkontoer
                  </th>
                </tr>
              </thead>
              <tbody>
                {HOVEDKONTO_KATEGORIER.map((kategori) => {
                  const hk = kontoplan.data.hovedkontoer.find((h) => h.kategori === kategori);
                  return (
                    <tr key={kategori} className="border-b border-kant/60 last:border-0">
                      <th scope="row" className="py-2 text-left font-normal text-tekst">
                        {KATEGORINAVN[kategori]}
                      </th>
                      <td className="py-2 text-right tabular-nums text-tekst-dempet">
                        {hk?.artsgrupper.length ?? 0}
                      </td>
                      <td className="py-2 text-right tabular-nums text-tekst-dempet">
                        {hk?.artsgrupper.reduce((n, a) => n + a.underkontoer.length, 0) ?? 0}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          ) : null}
        </Kort>

        <Kort
          tittel="Funksjonsflagg"
          beskrivelse="Hva som er slått på for denne kommunen."
          className="md:col-span-2"
        >
          {flagg.isPending ? <Laster hva="funksjonsflagg" /> : null}
          {flagg.isError ? <Feilmelding feil={flagg.error} /> : null}
          {flagg.data ? (
            <ul className="grid gap-2 sm:grid-cols-2">
              {flagg.data.map((f) => (
                <li key={f.nokkel} className="flex items-center gap-2 text-sm">
                  {/* The state is carried by text, not by colour alone (WCAG 1.4.1). */}
                  <span
                    className={
                      f.aktivert
                        ? 'rounded bg-positiv-svak px-2 py-0.5 text-xs font-medium text-positiv'
                        : 'rounded bg-flate-dempet px-2 py-0.5 text-xs font-medium text-tekst-dempet'
                    }
                  >
                    {f.aktivert ? 'På' : 'Av'}
                  </span>
                  <span className="text-tekst">{f.beskrivelse}</span>
                </li>
              ))}
            </ul>
          ) : null}
        </Kort>
      </div>
    </>
  );
}
